import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from '../firebase.js';
import { logout } from '../store/authSlice.js';
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import "../App.css";

const Profile = () => {
  const user = useSelector((s) => s.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.uid && db) {
        try {
          const userRef = ref(db, `users/${user.uid}`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            setUserData(snapshot.val());
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      setLoading(false);
    };

    fetchUserData();
  }, [user]);

  const displayUser = userData || user;

  const getAvatarUrl = () => {
    const photoURL = user?.photoURL || userData?.photoURL;
    if (photoURL) return photoURL;
    const seed = displayUser?.email ? displayUser.email.split('@')[0] : 'user';
    return `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${seed}`;
  };

  const isGoogleUser = user?.photoURL?.includes("googleusercontent");
  const colors = ["#984A1F","#2D6A4F","#1565C0","#6A1B9A","#C62828"];
  const avatarColor = colors[(displayUser?.email?.charCodeAt(0) || 0) % colors.length];
  const initial = displayUser?.name?.charAt(0)?.toUpperCase() || displayUser?.email?.charAt(0)?.toUpperCase() || "U";

  const ProfileAvatar = () => {
    const [failed, setFailed] = useState(false);
    return (
      <div className="user-avatar-wrap" style={{ width: 80, height: 80, flexShrink: 0 }}>
        {failed ? (
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: avatarColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 32, fontWeight: 700, color: "#fff" }}>{initial}</div>
        ) : (
          <img src={getAvatarUrl()} alt="avatar" className="rounded-circle"
            style={{ width: 80, height: 80, objectFit: "cover" }}
            onError={() => setFailed(true)} />
        )}
        {isGoogleUser && <span className="user-avatar-g-badge" title="Signed in with Google">G</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      dispatch(logout());
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Error logging out");
    }
  };

  if (!user) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="card border-0 shadow-sm rounded-4 p-5">
              <i className="bi bi-person-x display-4 text-muted mb-3"></i>
              <h3 className="fw-bold mb-3">Not Signed In</h3>
              <p className="text-muted mb-4">Please sign in to view your profile</p>
              <button
                className="btn btn-dark rounded-pill px-4 py-3"
                onClick={() => navigate("/signin")}
              >
                <i className="bi bi-person-circle me-2"></i>
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            {/* Header */}
            <div className="bg-dark text-white p-4" style={{ background: "linear-gradient(135deg, #2f0f0c, #4a1a15)" }}>
              <div className="d-flex align-items-center">
                <ProfileAvatar />
                <div className="ms-4">
                  <h2 className="fw-bold mb-1">{displayUser.name || "User"}</h2>
                  <p className="mb-0 opacity-75">
                    <i className="bi bi-envelope me-2"></i>
                    {displayUser.email}
                  </p>
                  {isGoogleUser ? (
                    <span style={{ fontSize: 12, background: "rgba(255,255,255,0.2)", borderRadius: 20,
                      padding: "2px 10px", marginTop: 6, display: "inline-block", fontWeight: 600 }}>
                      🔵 Signed in with Google
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, background: "rgba(255,255,255,0.2)", borderRadius: 20,
                      padding: "2px 10px", marginTop: 6, display: "inline-block", fontWeight: 600 }}>
                      ✉️ Email Account
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="p-4">
              <h5 className="fw-bold mb-4">
                <i className="bi bi-person-lines-fill me-2 text-primary"></i>
                Profile Information
              </h5>

              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary rounded-circle p-2 me-3">
                        <i className="bi bi-person text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">Display Name</small>
                        <div className="fw-semibold">{displayUser.name || "Not set"}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-success rounded-circle p-2 me-3">
                        <i className="bi bi-envelope text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">Email Address</small>
                        <div className="fw-semibold">{displayUser.email}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-info rounded-circle p-2 me-3">
                        <i className="bi bi-shield-check text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">Email Verified</small>
                        <div className="fw-semibold">
                          {displayUser.emailVerified !== undefined ? (
                            displayUser.emailVerified ? (
                              <span className="text-success">
                                <i className="bi bi-check-circle me-1"></i>
                                Verified
                              </span>
                            ) : (
                              <span className="text-warning">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                Not Verified
                              </span>
                            )
                          ) : (
                            <span className="text-muted">Unknown</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-warning rounded-circle p-2 me-3">
                        <i className="bi bi-calendar text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">Member Since</small>
                        <div className="fw-semibold">
                          {displayUser.createdAt ? 
                            new Date(displayUser.createdAt).toLocaleDateString() : 
                            "Unknown"
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-secondary rounded-circle p-2 me-3">
                        <i className="bi bi-clock text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">Last Sign In</small>
                        <div className="fw-semibold">
                          {displayUser.lastLoginAt ? 
                            new Date(displayUser.lastLoginAt).toLocaleDateString() : 
                            "Today"
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-dark rounded-circle p-2 me-3">
                        <i className="bi bi-key text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">User ID</small>
                        <div className="fw-semibold small">{(displayUser.uid || user?.uid || 'unknown')?.slice(0, 12)}...</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card bg-light border-0 rounded-3 p-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary rounded-circle p-2 me-3">
                        <i className="bi bi-telephone text-white"></i>
                      </div>
                      <div>
                        <small className="text-muted">Phone Number</small>
                        <div className="fw-semibold">{displayUser.phone || "Not provided"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-5">
                <h6 className="fw-bold mb-3">
                  <i className="bi bi-lightning me-2 text-warning"></i>
                  Quick Actions
                </h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <button 
                      className="btn btn-outline-primary w-100 rounded-pill py-3"
                      onClick={() => navigate("/my-orders")}
                    >
                      <i className="bi bi-bag me-2"></i>
                      My Orders
                    </button>
                  </div>
                  <div className="col-md-4">
                    <button 
                      className="btn btn-outline-success w-100 rounded-pill py-3"
                      onClick={() => navigate("/addresses")}
                    >
                      <i className="bi bi-geo-alt me-2"></i>
                      Addresses
                    </button>
                  </div>
                  <div className="col-md-4">
                    <button 
                      className="btn btn-outline-info w-100 rounded-pill py-3"
                      onClick={() => navigate("/cart")}
                    >
                      <i className="bi bi-cart me-2"></i>
                      My Cart
                    </button>
                  </div>
                </div>
              </div>

              {/* Logout Button */}
              <div className="mt-5 pt-4 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="fw-bold mb-1">Account Management</h6>
                    <small className="text-muted">Sign out of your account</small>
                  </div>
                  <button 
                    className="btn btn-danger rounded-pill px-4 py-2"
                    onClick={handleLogout}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;